import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { axiosDocChatInstance } from '../../lib/axiosDocChatInstance';
import { TAKE_TOUR_TYPES } from '../TakeTour/constants/constants';
import { dummyData } from '../TakeTour/constants/dummyData';
import { useTakeTour } from '../TakeTour/hooks/useTakeTour';

const getDocumentLink = async (documentId, signal) => {
  const { data } = await axiosDocChatInstance.get(`/api/documents/${documentId}/`, { signal });
  return data;
};

export const useSelectedUploadData = () => {
  const { isOpen, tourType } = useTakeTour();
  const { records } = useSelector((state) => state.docChatData);
  const calculatedRecords = tourType === TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER ? dummyData : records;

  const [searchParams] = useSearchParams();
  const uuid = searchParams.get('uuid');
  const position = searchParams.get('position') ? Number(searchParams.get('position')) : null;

  const selectedUploadData = useMemo(() => {
    const record = calculatedRecords?.find(
      (v) =>
        (v.collection_id && v.collection_id === uuid) || (v.document_id && v.document_id === uuid)
    );
    if (!record) return { recordType: '', record: {} };
    const recordType = record.collection_id ? 'COLLECTION' : 'DOCUMENT';
    const selectedDoc = recordType === 'COLLECTION' ? record.documents[position || 0] : record;

    return { recordType, record, selectedDoc };
  }, [uuid, calculatedRecords, position]);

  const selectedDocId = selectedUploadData.selectedDoc?.document_id;

  const {
    fetchStatus: selectedDocLinkFetchStatus,
    error: selectedDocLinkError,
    data: selectedDocLinkData,
  } = useQuery({
    queryKey: ['document-link', selectedDocId, isOpen],
    queryFn: ({ signal }) => getDocumentLink(selectedDocId, signal),
    enabled: !!selectedDocId && !isOpen,
    staleTime: 55 * 60 * 1000, // 55 minutes
    gcTime: 55 * 60 * 1000, // 55 minutes
  });

  const selectedDocLinkLoading = selectedDocLinkFetchStatus === 'fetching';

  const selectedDocLinkObj = useMemo(() => {
    if (tourType === TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER) {
      return {
        selectedDocLink: dummyData[0].pdf_link,
        selectedDocLinkLoading: false,
        selectedDocLinkError: null,
      };
    }
    return {
      selectedDocLink: selectedDocLinkData?.presigned_url,
      selectedDocLinkLoading,
      selectedDocLinkError,
    };
  }, [selectedDocLinkData?.presigned_url, selectedDocLinkLoading, selectedDocLinkError, tourType]);

  return {
    ...selectedUploadData,
    ...selectedDocLinkObj,
    position,
    records: calculatedRecords,
    uuid,
  };
};
